import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';

import * as legalApi from '../../api/legal';
import type { LegalDocumentSummary } from '../../api/legal';
import { extractErrorMessage } from '../../api/errors';
import { useAuth } from '../../auth/AuthContext';
import { LegalLinks } from './LegalConsent';

/**
 * The re-accept step after sign-in (LEGAL-TERMS). When a platform admin
 * publishes a material change — or the first version — signed-in users see
 * this before the app: read the new text, tick, accept; or sign out. A
 * support (act-as) session is never asked: the server reports nothing
 * pending for it.
 */
export function LegalGate() {
  const { logout } = useAuth();
  const [pending, setPending] = useState<LegalDocumentSummary[]>([]);
  const [ticked, setTicked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    legalApi
      .getLegalStatus()
      .then((s) => {
        if (!cancelled) setPending(s.pending);
      })
      // Not knowing must not lock anyone out of the app.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  if (pending.length === 0) return null;

  const accept = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await legalApi.acceptLegal(pending.map((d) => d.id));
      setPending(res.pending);
      setTicked(false);
    } catch (err) {
      setError(extractErrorMessage(err, 'could not record your acceptance'));
    } finally {
      setSaving(false);
    }
  };

  const updated = pending.some((d) => d.version > 1);
  return (
    <Dialog open maxWidth="sm" fullWidth aria-labelledby="legal-gate-title">
      <DialogTitle id="legal-gate-title">
        {updated ? 'We have updated our terms' : 'Please review our terms'}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          To keep using 10NEWTONS, please read and accept the{' '}
          <LegalLinks docs={pending} />. They open in a new tab.
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={ticked}
              onChange={(e) => setTicked(e.target.checked)}
              disabled={saving}
            />
          }
          label={
            <Typography variant="body2">
              I have read and agree to the <LegalLinks docs={pending} />.
            </Typography>
          }
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => void logout()} disabled={saving}>
          Sign out
        </Button>
        <Button
          variant="contained"
          onClick={() => void accept()}
          disabled={!ticked || saving}
        >
          {saving ? 'Saving…' : 'Accept and continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
